import React from "react";
import {useState, useContext, useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./TopicDescriptionCreation.css";
import {Resolution} from "../../../logic/API";
import copyIcon from "../../../images/copy.svg";
import {
  AuthMethod,
  TopicDescription,
  TopicDescriptionPushTransfer,
  TopicDescriptionPushTransferResponse,
} from "../../../logic/APITypes";
import {
  TextInput,
  ModalContext,
  RadioButtonGroup,
} from "@spg/component_library";
import {copyTextToClipboard} from "../../../logic/clipboard";

/**
 * Controls the transfer section when creating, viewing and editing topic descriptions with push direction.
 */
function PushTransferSection(props: {
  alternative: "create" | "view" | "edit";
  authentication: Omit<AuthMethod, "NULL">;
  setAuthentication: (authentication: AuthMethod) => void;
  initialTopic: Resolution<TopicDescription> | undefined;
  creationResponse?: TopicDescriptionPushTransferResponse;
  onCloseModal: () => void;
}) {
  const intl = useIntl();
  const modalContext = useContext(ModalContext);
  const [urlCopied, setUrlCopied] = useState<"globalG" | "success" | "failure">(
    "globalG",
  );
  const [nameCopied, setNameCopied] = useState<
    "globalG" | "success" | "failure"
  >("globalG");
  const [secretCopied, setSecretCopied] = useState<
    "globalG" | "success" | "failure"
  >("globalG");

  const [urlCopiedTimeout, setUrlCopiedTimeout] =
    useState<ReturnType<typeof setTimeout>>();
  const [nameCopiedTimeout, setNameCopiedTimeout] =
    useState<ReturnType<typeof setTimeout>>();
  const [secretCopiedTimeout, setSecretCopiedTimeout] =
    useState<ReturnType<typeof setTimeout>>();

  const initialTopic = props.initialTopic?.payload;
  const placeholder = props.initialTopic?.unresolved
    ? intl.formatMessage({
        id: "topicDescriptionCreation.inputLabel.placeholder.loading",
      })
    : undefined;

  const clickCopy = (
    setCopy: (
      value: React.SetStateAction<"globalG" | "success" | "failure">,
    ) => void,
    copyTimeout: ReturnType<typeof setTimeout> | undefined,
    setCopyTimeout: React.Dispatch<
      React.SetStateAction<NodeJS.Timeout | undefined>
    >,
    text?: string,
  ) => {
    if (!text) return;
    copyTextToClipboard(text).then(
      () => {
        clearTimeout(copyTimeout);
        setCopy("success");
        const timerId = setTimeout(() => {
          setCopy("globalG");
        }, 1000);
        setCopyTimeout(timerId);
      },
      () => {
        clearTimeout(copyTimeout);
        setCopy("failure");
        const timerId = setTimeout(() => {
          setCopy("globalG");
        }, 1000);
        setCopyTimeout(timerId);
      },
    );
  };

  useEffect(() => {
    if (props.creationResponse) {
      modalContext.showModal({
        idBase: "topicDescriptionPushInfo",
        headline: intl.formatMessage({
          id: "topicDescriptionCreation.modal.pushInfo.headline",
        }),
        body: (
          <div className="topicDescriptionModalBody">
            <FormattedMessage
              id={
                props.creationResponse.authentication.method === "BASIC"
                  ? "topicDescriptionCreation.modal.pushInfo.text.basic"
                  : "topicDescriptionCreation.modal.pushInfo.text.token"
              }
              values={{b: (chunks) => <b>{chunks}</b>}}
            />
          </div>
        ),
        primaryButton: {
          name: intl.formatMessage({
            id: "topicDescriptionCreation.modal.pushInfo.close",
          }),
          onClick: props.onCloseModal,
        },
        additionalContent: (
          <div className="topicDescriptionCreationInfoModalWrapper">
            <TextInput
              idBase="topicDescriptionPushInfoModalUrl"
              label={intl.formatMessage({
                id: "topicDescriptionCreation.modal.pushInfo.url",
              })}
              value={props.creationResponse.url}
              setValue={() => undefined}
              isReadOnly
              trailingIconButton={{
                iconProps: {
                  title: intl.formatMessage({
                    id: "topicDescriptionCreation.modal.pushInfo.copyUrl",
                  }),
                  href: copyIcon + "#globalG",
                  strokeColor: "#101223",
                },
                idBase: "topicDescriptionPushInfoModalCopyUrl",
                onClick: () => {
                  clickCopy(
                    setUrlCopied,
                    urlCopiedTimeout,
                    setUrlCopiedTimeout,
                    props.creationResponse?.url,
                  );
                },
              }}
            />
            <TextInput
              idBase="topicDescriptionPushInfoModalName"
              label={intl.formatMessage({
                id:
                  props.creationResponse.authentication.method === "BASIC"
                    ? "topicDescriptionCreation.modal.pushInfo.name.basic"
                    : "topicDescriptionCreation.modal.pushInfo.name.token",
              })}
              value={
                props.creationResponse.authentication.method === "BASIC"
                  ? props.creationResponse.authentication.username
                  : props.creationResponse.authentication.name
              }
              setValue={() => undefined}
              trailingIconButton={{
                iconProps: {
                  title: intl.formatMessage({
                    id:
                      props.creationResponse.authentication.method === "BASIC"
                        ? "topicDescriptionCreation.modal.pushInfo.copyUsername"
                        : "topicDescriptionCreation.modal.pushInfo.copyTokenname",
                  }),
                  href: copyIcon + "#globalG",
                  strokeColor: "#101223",
                },
                idBase: "topicDescriptionPushInfoModalCopyName",
                onClick: () => {
                  if (!props.creationResponse) return;
                  clickCopy(
                    setNameCopied,
                    nameCopiedTimeout,
                    setNameCopiedTimeout,
                    props.creationResponse.authentication.method === "BASIC"
                      ? props.creationResponse.authentication.username
                      : props.creationResponse.authentication.name,
                  );
                },
              }}
              isReadOnly
            />
            <TextInput
              idBase="topicDescriptionPushInfoModalSecret"
              label={intl.formatMessage({
                id:
                  props.creationResponse.authentication.method === "BASIC"
                    ? "topicDescriptionCreation.modal.pushInfo.secret.password"
                    : "topicDescriptionCreation.modal.pushInfo.secret.token",
              })}
              value={
                props.creationResponse.authentication.method === "BASIC"
                  ? (props.creationResponse.authentication.password ?? "")
                  : (props.creationResponse.authentication.token ?? "")
              }
              setValue={() => undefined}
              trailingIconButton={{
                iconProps: {
                  title: intl.formatMessage({
                    id:
                      props.creationResponse.authentication.method === "BASIC"
                        ? "topicDescriptionCreation.modal.pushInfo.copyPassword"
                        : "topicDescriptionCreation.modal.pushInfo.copyToken",
                  }),
                  href: copyIcon + "#globalG",
                  strokeColor: "#101223",
                },
                idBase: "topicDescriptionPushInfoModalCopySecret",
                onClick: () => {
                  if (!props.creationResponse) return;
                  clickCopy(
                    setSecretCopied,
                    secretCopiedTimeout,
                    setSecretCopiedTimeout,
                    props.creationResponse.authentication.method === "BASIC"
                      ? props.creationResponse.authentication.password
                      : props.creationResponse.authentication.token,
                  );
                },
              }}
            />
          </div>
        ),
      });
    }
  }, [props.creationResponse]);

  useEffect(
    () => () => {
      if (urlCopiedTimeout) clearTimeout(urlCopiedTimeout);
      if (secretCopiedTimeout) clearTimeout(secretCopiedTimeout);
      if (nameCopiedTimeout) clearTimeout(nameCopiedTimeout);
    },
    [],
  );
  useEffect(() => {
    const embeddedIcon = document.querySelector(
      "#topicDescriptionPushInfoModalCopyUrlIconButton > svg > use",
    );
    if (!embeddedIcon) return;
    (embeddedIcon as SVGUseElement).setAttribute(
      "href",
      copyIcon + "#" + urlCopied,
    );
  }, [urlCopied]);
  useEffect(() => {
    const embeddedIcon = document.querySelector(
      "#topicDescriptionPushInfoModalCopyNameIconButton > svg > use",
    );
    if (!embeddedIcon) return;
    (embeddedIcon as SVGUseElement).setAttribute(
      "href",
      copyIcon + "#" + nameCopied,
    );
  }, [nameCopied]);
  useEffect(() => {
    const embeddedIcon = document.querySelector(
      "#topicDescriptionPushInfoModalCopySecretIconButton > svg > use",
    );
    if (!embeddedIcon) return;
    (embeddedIcon as SVGUseElement).setAttribute(
      "href",
      copyIcon + "#" + secretCopied,
    );
  }, [secretCopied]);

  return (
    <>
      {props.alternative === "create" && (
        <>
          <RadioButtonGroup
            label={"Authentifizierung"}
            idBase={"authentication"}
            name={"authentication"}
            options={{
              BASIC: intl.formatMessage({
                id: "topicDescriptionCreation.option.authentication.basic",
              }),
              TOKEN_HEADER: intl.formatMessage({
                id: "topicDescriptionCreation.option.authentication.token.header",
              }),
              TOKEN_QUERY: intl.formatMessage({
                id: "topicDescriptionCreation.option.authentication.token.query",
              }),
            }}
            value={props.authentication as AuthMethod}
            setValue={(newValue: string | null) => {
              if (
                newValue === null ||
                !["BASIC", "TOKEN_HEADER", "TOKEN_QUERY"].includes(newValue)
              ) {
                return;
              }
              props.setAuthentication(newValue as AuthMethod);
            }}
          />
          <div className="topicDescriptionCreationPushInfo">
            <FormattedMessage
              id={
                props.authentication === "BASIC"
                  ? "topicDescriptionCreation.push.information.generate.basic"
                  : "topicDescriptionCreation.push.information.generate.token"
              }
            />
          </div>
          <div className="topicDescriptionCreationPushWarning">
            <FormattedMessage
              id={
                props.authentication === "BASIC"
                  ? "topicDescriptionCreation.push.information.access.basic"
                  : "topicDescriptionCreation.push.information.access.token"
              }
            />
          </div>
        </>
      )}
      {props.alternative !== "create" && (
        <div className="topicDescriptionCreationSpecificsContainer">
          <TextInput
            label={intl.formatMessage({
              id: "topicDescriptionCreation.modal.pushInfo.url",
            })}
            placeholder={placeholder || ""}
            idBase={"topicDescriptionCreationURL"}
            value={
              (
                initialTopic?.transfer as
                  | TopicDescriptionPushTransfer
                  | undefined
              )?.url || ""
            }
            setValue={() => null}
            isReadOnly
          />
          <RadioButtonGroup
            label={"Authentifizierung"}
            idBase={"authentication"}
            name={"authentication"}
            options={{
              BASIC: intl.formatMessage({
                id: "topicDescriptionCreation.option.authentication.basic",
              }),
              TOKEN_HEADER: intl.formatMessage({
                id: "topicDescriptionCreation.option.authentication.token.header",
              }),
              TOKEN_QUERY: intl.formatMessage({
                id: "topicDescriptionCreation.option.authentication.token.query",
              }),
            }}
            value={props.authentication as AuthMethod}
            setValue={(newValue: string | null) => {
              if (
                newValue === null ||
                !["BASIC", "TOKEN_HEADER", "TOKEN_QUERY"].includes(newValue)
              ) {
                return;
              }
              props.setAuthentication(newValue as AuthMethod);
            }}
            isReadOnly
          />
          <div className="topicDescriptionCreationPushInaccessible">
            <FormattedMessage
              id={
                props.authentication === "BASIC"
                  ? "topicDescriptionCreation.push.information.inaccessible.basic"
                  : "topicDescriptionCreation.push.information.inaccessible.token"
              }
            />
          </div>
        </div>
      )}
    </>
  );
}

export {PushTransferSection};
